import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { toModifiedProcessModelId } from "@/api/identifiers";
import { qk } from "@/api/query-keys";
import { notifySuccess } from "@/lib/notify";

/**
 * モデルを削除する。
 *
 * 案件（process instance）が 1 件でもあると backend が 400 `existing_instances`
 * を返す。ここでは握り潰さずそのまま投げ、画面側で理由を出し分ける。
 */
export async function deleteProcessModel(
  client: ApiClient,
  processModelId: string,
): Promise<void> {
  await client.del(
    `/process-models/${toModifiedProcessModelId(processModelId)}`,
  );
}

/**
 * グループを削除する。
 *
 * backend は配下のモデルごと消す（rmtree。案件のあるモデルが無ければ止めない）。
 * 画面は空のグループにだけ導線を出すが、一覧はキャッシュ（最大 5 分）で、読み込み中・
 * 取得失敗・件数上限でも空に見えうる。そこで消す直前に配下のモデル数を backend に
 * 問い合わせ直し、1 件でもあれば消さない。
 */
export async function deleteProcessGroup(
  client: ApiClient,
  processGroupId: string,
): Promise<void> {
  const inGroup = await client.get<{
    readonly pagination?: { readonly total?: number };
  }>("/process-models", {
    query: {
      // modified id ではなく / 区切りのまま渡す（backend がパスに直す）
      process_group_identifier: processGroupId,
      recursive: true,
      per_page: 1,
      page: 1,
    },
  });
  if ((inGroup.pagination?.total ?? 0) > 0) {
    throw new GroupNotEmptyError(processGroupId);
  }

  await client.del(
    `/process-groups/${toModifiedProcessModelId(processGroupId)}`,
  );
}

/** 消そうとしたグループの配下にモデルがあった（一覧が古かった）。 */
export class GroupNotEmptyError extends Error {
  readonly processGroupId: string;

  constructor(processGroupId: string) {
    super(`process group ${processGroupId} has models`);
    this.name = "GroupNotEmptyError";
    this.processGroupId = processGroupId;
  }
}

/** 作成・削除の後はモデル一覧・グループ一覧の両方が変わる。 */
export function useInvalidateDesignLists(): () => Promise<void> {
  const queryClient = useQueryClient();

  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: qk.processModels._root() }),
      queryClient.invalidateQueries({ queryKey: qk.processGroups._root() }),
    ]);
  };
}

export type DeleteModelInput = {
  readonly processModelId: string;
  /** トーストに出す名前（削除すると画面から消えるので、何が消えたかを残す）。 */
  readonly displayName: string;
};

export function useDeleteModel(): UseMutationResult<
  void,
  Error,
  DeleteModelInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");

  return useMutation({
    mutationFn: (input: DeleteModelInput) =>
      deleteProcessModel(client, input.processModelId),
    onSuccess: (_result, input) => {
      notifySuccess(t("toast.modelDeleted", { name: input.displayName }));
    },
    // 一覧は成否によらず読み直す（他の人が先に消していた場合、古いまま残らないように）。
    // ただし消そうとしたモデルの詳細・ファイルは読み直さない。画面はまだそのモデルを
    // 開いているので、取り直すと 404 を受けて一瞬エラーを出してしまう（削除後は
    // 未選択の一覧へ移るので、古いキャッシュは使われないまま捨てられる）
    onSettled: async (_result, _error, input) => {
      const modifiedId = toModifiedProcessModelId(input.processModelId);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: qk.processModels._root(),
          predicate: (query) => !isModelDetailQuery(query.queryKey, modifiedId),
        }),
        queryClient.invalidateQueries({ queryKey: qk.processGroups._root() }),
      ]);
    },
  });
}

/** use-process-model の詳細・ファイルの queryKey（`["process-models", "detail" | "file", modifiedId, ...]`）。 */
function isModelDetailQuery(
  queryKey: readonly unknown[],
  modifiedId: string,
): boolean {
  return (
    (queryKey[1] === "detail" || queryKey[1] === "file") &&
    queryKey[2] === modifiedId
  );
}

export type DeleteGroupInput = {
  readonly processGroupId: string;
  /** トーストに出す名前（左の一覧の見出しと同じ `人事 / 社内` の形）。 */
  readonly label: string;
};

export function useDeleteGroup(): UseMutationResult<
  void,
  Error,
  DeleteGroupInput
> {
  const client = useApiClient();
  const invalidate = useInvalidateDesignLists();
  const { t } = useTranslation("common");

  return useMutation({
    mutationFn: (input: DeleteGroupInput) =>
      deleteProcessGroup(client, input.processGroupId),
    onSuccess: (_result, input) => {
      notifySuccess(t("toast.groupDeleted", { name: input.label }));
    },
    // 配下にモデルがあって止めた場合も、一覧を読み直して実態に合わせる
    onSettled: invalidate,
  });
}
